import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Loader2, ChevronRight, ArrowLeft } from 'lucide-react';
import { api } from '../services/api';
import { useSellingStore } from '../store/selling';

export function MessagesPage() {
  const navigate = useNavigate();
  const { openSellModal } = useSellingStore();

  const { data: messages, isLoading } = useQuery({
    queryKey: ['discogs-messages'],
    queryFn: () => api.discogs.getAllMessages(),
  });

  const handleMessageClick = (itemId: string) => {
    // Navigate to selling page and open the item's modal
    openSellModal(itemId);
    navigate('/selling');
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/selling')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
          <p className="text-gray-600">Discogs buyer conversations</p>
        </div>
      </div>

      {/* Messages List */}
      {isLoading ? (
        <div className="card p-8 text-center text-gray-500">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
          Loading messages...
        </div>
      ) : !messages || messages.length === 0 ? (
        <div className="card p-8 text-center">
          <MessageCircle className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Messages</h3>
          <p className="text-gray-600">
            When buyers message you about your Discogs listings, they'll appear here.
          </p>
        </div>
      ) : (
        <div className="card divide-y divide-gray-100">
          {messages.map((msg) => (
            <button
              key={`${msg.orderId}-${msg.itemId}`}
              onClick={() => handleMessageClick(msg.itemId)}
              className="w-full p-4 flex items-center gap-4 hover:bg-gray-50 text-left transition-colors"
            >
              {/* Item Thumbnail */}
              <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                {msg.itemThumbnail ? (
                  <img
                    src={msg.itemThumbnail}
                    alt={msg.itemTitle}
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <span className="text-2xl text-gray-300">📦</span>
                )}
              </div>

              {/* Message Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-gray-900 truncate">{msg.itemTitle}</h3>
                  {msg.hasUnread && (
                    <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                  <span className="font-medium">{msg.buyer.username}</span>
                  <span>·</span>
                  <span>{formatTimestamp(msg.latestMessage.timestamp)}</span>
                </div>
                <p className="text-sm text-gray-600 truncate">
                  {msg.latestMessage.from === msg.buyer.username ? '' : 'You: '}
                  {msg.latestMessage.message}
                </p>
              </div>

              {/* Message Count & Arrow */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-gray-400">{msg.messageCount} msg{msg.messageCount !== 1 ? 's' : ''}</span>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
